import React, { useState, useRef, useEffect, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import { RegularText, MediumText, DemiLightText } from '@/lib/Text';
import { Row } from '@/lib/utils';
import SvgIcon from '@/components/SvgIcon';
import { colors } from '@/lib/colors';

dayjs.locale('ko');

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

export interface DatePickerTriggerProps {
    formattedDate: string;
    selectedDate: Dayjs;
    isOpen: boolean;
    toggle: () => void;
    isHovered: boolean;
}

interface DatePickerProps {
    value?: Dayjs;
    onChange: (date: Dayjs) => void;
    /** 날짜 표시 영역을 커스텀하는 렌더 함수 */
    renderTrigger?: (props: DatePickerTriggerProps) => React.ReactNode;
    /** dayjs format 문자열 (기본값: 'YYYY. MM. DD (dd)') */
    dateFormat?: string;
    /** 드롭다운 위치 className */
    dropdownClassName?: string;
    /** 컨테이너 className */
    className?: string;
    /** 외부 클릭 시 닫기 여부 (기본값: true) */
    closeOnOutsideClick?: boolean;
}

const DatePicker = ({
    value,
    onChange,
    renderTrigger,
    dateFormat = 'YYYY. MM. DD (dd)',
    dropdownClassName = 'absolute top-[42px] left-0',
    className,
    closeOnOutsideClick = true,
}: DatePickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewMonth, setViewMonth] = useState((value ?? dayjs()).startOf('month'));
    const [hoveredCell, setHoveredCell] = useState<string | null>(null);
    const [isTriggerHovered, setIsTriggerHovered] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedDate = value ?? dayjs();
    const formattedDate = selectedDate.format(dateFormat);

    // value가 바뀌면 viewMonth도 동기화
    useEffect(() => {
        if (value) {
            setViewMonth(value.startOf('month'));
        }
    }, [value?.format('YYYY-MM')]);

    // 외부 클릭 시 닫기
    useEffect(() => {
        if (!closeOnOutsideClick || !isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [closeOnOutsideClick, isOpen]);

    const toggle = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const handleDateSelect = (date: Dayjs, isCurrentMonth: boolean) => {
        onChange(date);
        if (!isCurrentMonth) {
            setViewMonth(date.startOf('month'));
        }
        setIsOpen(false);
    };

    const renderCalendar = () => {
        const startOfMonth = viewMonth.startOf('month');
        const daysInMonth = viewMonth.daysInMonth();
        const startDayOfWeek = startOfMonth.day();

        const prevMonth = viewMonth.subtract(1, 'month');
        const daysInPrevMonth = prevMonth.daysInMonth();

        const cells: { date: Dayjs; isCurrentMonth: boolean }[] = [];

        for (let i = startDayOfWeek - 1; i >= 0; i--) {
            cells.push({ date: prevMonth.date(daysInPrevMonth - i), isCurrentMonth: false });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            cells.push({ date: viewMonth.date(d), isCurrentMonth: true });
        }
        const nextMonth = viewMonth.add(1, 'month');
        const remaining = (7 - (cells.length % 7)) % 7;
        for (let d = 1; d <= remaining; d++) {
            cells.push({ date: nextMonth.date(d), isCurrentMonth: false });
        }

        const weeks: (typeof cells)[] = [];
        for (let i = 0; i < cells.length; i += 7) {
            weeks.push(cells.slice(i, i + 7));
        }

        const today = dayjs();

        return (
            <div className="pb-[8px]">
                {/* Month navigation */}
                <Row className="items-center justify-center gap-[20px] py-[14px]">
                    <div
                        className="cursor-pointer p-[4px]"
                        onClick={() => setViewMonth(viewMonth.subtract(1, 'month'))}
                    >
                        <SvgIcon
                            name="SvgLeftStrokeArrow"
                            size={12}
                            fill={colors.trans}
                            stroke="#191F28"
                            strokeWidth={1.5}
                        />
                    </div>
                    <MediumText fontSize={16} lineHeight={24} color="#191F28">
                        {viewMonth.format('YYYY.MM')}
                    </MediumText>
                    <div className="cursor-pointer p-[4px]" onClick={() => setViewMonth(viewMonth.add(1, 'month'))}>
                        <SvgIcon
                            name="SvgRightStrokeArrow"
                            size={12}
                            fill={colors.trans}
                            stroke="#191F28"
                            strokeWidth={1.5}
                        />
                    </div>
                </Row>

                {/* Day headers */}
                <div className="grid grid-cols-7 mb-[2px]">
                    {DAY_LABELS.map((label, i) => (
                        <div key={label} className="flex items-center justify-center h-[30px]">
                            <DemiLightText fontSize={13} lineHeight={20} color={i === 0 ? '#FF4D4F' : '#8B9194'}>
                                {label}
                            </DemiLightText>
                        </div>
                    ))}
                </div>

                {/* Date cells */}
                {weeks.map((week, wi) => (
                    <div key={wi} className="grid grid-cols-7">
                        {week.map((cell, di) => {
                            const cellKey = `${wi}-${di}`;
                            const isToday = cell.date.isSame(today, 'day');
                            const isSelected = cell.date.isSame(selectedDate, 'day') && cell.isCurrentMonth;
                            const isHovered = hoveredCell === cellKey && !isSelected;
                            const isSunday = cell.date.day() === 0;

                            const bgClass = isSelected ? 'bg-[#E8EDF2]' : isHovered ? 'bg-[#E8EDF2]' : '';

                            const borderClass =
                                isToday && !isSelected && !isHovered ? 'border border-solid border-primary' : '';

                            const textColor =
                                isSelected || isHovered
                                    ? '#3182F6'
                                    : !cell.isCurrentMonth
                                      ? '#D1D5D9'
                                      : isSunday
                                        ? '#FF4D4F'
                                        : '#191F28';

                            return (
                                <div
                                    key={di}
                                    className="flex items-center justify-center h-[36px] cursor-pointer"
                                    onMouseEnter={() => setHoveredCell(cellKey)}
                                    onMouseLeave={() => setHoveredCell(null)}
                                    onClick={() => handleDateSelect(cell.date, cell.isCurrentMonth)}
                                >
                                    <div
                                        className={`w-[32px] h-[32px] flex items-center justify-center rounded-full ${bgClass} ${borderClass}`}
                                    >
                                        <RegularText fontSize={14} lineHeight={20} color={textColor}>
                                            {cell.date.date()}
                                        </RegularText>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    const defaultTrigger = (
        <div
            className={`px-[14px] py-[8px] border border-solid rounded-[6px] cursor-pointer transition-colors hover:border-primary ${
                isOpen ? 'border-primary' : 'border-[#E6E8EA]'
            }`}
            onClick={toggle}
        >
            <RegularText fontSize={14} lineHeight={20} color="#191F28" letterSpacing={-0.4}>
                {formattedDate}
            </RegularText>
        </div>
    );

    return (
        <div
            ref={containerRef}
            className={`relative ${className ?? ''}`}
            onMouseEnter={() => setIsTriggerHovered(true)}
            onMouseLeave={() => setIsTriggerHovered(false)}
        >
            {renderTrigger
                ? renderTrigger({ formattedDate, selectedDate, isOpen, toggle, isHovered: isTriggerHovered })
                : defaultTrigger}

            {isOpen && (
                <div
                    className={`w-[300px] bg-white border border-solid border-[#E6E8EA] rounded-[12px] py-[4px] z-20 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] ${dropdownClassName}`}
                >
                    {renderCalendar()}
                </div>
            )}
        </div>
    );
};

export default DatePicker;
